import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Bare / Primed / Painted toggle for the product media gallery (blocks/sh-paint-toggle.liquid).
 *
 * The block renders a JSON map of media id -> finish ('bare' | 'primed' | 'painted' | 'any') worked out from each
 * image's alt text. This component reads it, finds the section's <media-gallery>, and:
 * - filters the desktop grid (`.media-gallery__grid`) to the chosen finish ('any' items always stay);
 * - jumps the slideshow (mobile, or a carousel gallery) to the first slide of that finish;
 * - keeps the pressed button in step when the shopper swipes the visible slideshow.
 * The gallery is replaced on variant changes, so the component re-binds after each product select.
 * It stays hidden when it can't find the gallery or fewer than two finishes are on the page.
 *
 * @typedef {Object} PaintToggleRefs
 * @property {HTMLButtonElement[]} buttons
 * @property {HTMLElement} caption
 *
 * @extends {Component<PaintToggleRefs>}
 */
class PaintToggle extends Component {
  requiredRefs = ['buttons', 'caption'];

  /** @type {Record<string, string>} */
  #states = {};

  /** @type {string | null} */
  #current = null;

  /** @type {boolean} */
  #filtered = false;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {AbortController | null} */
  #galleryController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#states = this.#readStates();
    this.#filtered = this.dataset.startFiltered === 'true';

    const section = this.closest('.shopify-section');
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    this.#bind();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    this.#galleryController?.abort();
    this.#galleryController = null;
  }

  /**
   * Shows one finish. Called from the buttons: on:click="/select/bare".
   * @param {string} state - 'bare', 'primed' or 'painted'
   */
  select(state) {
    if (typeof state !== 'string') return;
    const button = this.refs.buttons?.find((item) => item.dataset.state === state);
    if (!button || button.hidden) return;

    this.#filtered = true;
    this.#apply(state, { jump: true });
  }

  /** @returns {Record<string, string>} */
  #readStates() {
    const script = this.querySelector('script[data-sh-paint-states]');
    if (!script?.textContent) return {};
    try {
      const parsed = JSON.parse(script.textContent);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /** @returns {HTMLElement | null} */
  get #gallery() {
    const root = this.closest('.shopify-section') ?? document;
    const gallery = root.querySelector('media-gallery');
    return gallery instanceof HTMLElement ? gallery : null;
  }

  /**
   * @param {Element} item
   * @returns {string}
   */
  #stateOf(item) {
    const media = item.querySelector('[data-media-id]');
    const id = media instanceof HTMLElement ? media.dataset.mediaId : undefined;
    if (!id) return 'any';
    return this.#states[id] ?? 'any';
  }

  /**
   * @param {HTMLElement} gallery
   * @returns {HTMLElement | null}
   */
  #slideshowOf(gallery) {
    const slideshow = gallery.querySelector('slideshow-component');
    return slideshow instanceof HTMLElement ? slideshow : null;
  }

  /**
   * Slides in their current order, skipping hidden ones (matches the slideshow's own `slides` list).
   * @param {HTMLElement} gallery
   * @returns {HTMLElement[]}
   */
  #slidesOf(gallery) {
    const slideshow = this.#slideshowOf(gallery);
    if (!slideshow) return [];
    return /** @type {HTMLElement[]} */ (
      [...slideshow.querySelectorAll('slideshow-slide')].filter(
        (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal')
      )
    );
  }

  /**
   * @param {HTMLElement} gallery
   * @returns {HTMLElement[]}
   */
  #gridItemsOf(gallery) {
    return /** @type {HTMLElement[]} */ ([...gallery.querySelectorAll('.media-gallery__grid > li')]);
  }

  #bind() {
    this.#galleryController?.abort();
    this.#galleryController = new AbortController();

    const gallery = this.#gallery;
    if (!gallery) {
      this.hidden = true;
      return;
    }

    const slides = this.#slidesOf(gallery);
    const gridItems = this.#gridItemsOf(gallery);
    const items = slides.length > 0 ? slides : gridItems;
    const present = new Set(items.map((item) => this.#stateOf(item)).filter((state) => state !== 'any'));

    let visibleButtons = 0;
    for (const button of this.refs.buttons ?? []) {
      const available = present.has(button.dataset.state ?? '');
      button.hidden = !available;
      if (available) visibleButtons += 1;
    }

    if (visibleButtons < 2) {
      this.hidden = true;
      this.#clearGrid(gallery);
      return;
    }

    gallery.addEventListener('slideshow:select', this.#handleSlideSelect, { signal: this.#galleryController.signal });

    const firstState = items.map((item) => this.#stateOf(item)).find((state) => state !== 'any') ?? null;
    const initial = this.#current && present.has(this.#current) ? this.#current : firstState;
    this.hidden = false;
    if (initial) this.#apply(initial, { jump: false });
  }

  /**
   * @param {string} state
   * @param {{ jump: boolean }} options
   */
  #apply(state, { jump }) {
    const gallery = this.#gallery;
    const changed = state !== this.#current;
    this.#current = state;

    for (const button of this.refs.buttons ?? []) {
      button.setAttribute('aria-pressed', String(button.dataset.state === state));
    }

    // Only touch the live region when the words change, so binding on load doesn't announce anything
    const pressed = this.refs.buttons?.find((button) => button.dataset.state === state);
    const caption = pressed?.dataset.caption ?? '';
    if (this.refs.caption && changed && this.refs.caption.textContent?.trim() !== caption) {
      this.refs.caption.textContent = caption;
    }

    if (!gallery) return;

    this.#filterGrid(gallery, state);

    if (jump) this.#jump(gallery, state);
  }

  /**
   * @param {HTMLElement} gallery
   * @param {string} state
   */
  #filterGrid(gallery, state) {
    const grid = gallery.querySelector('.media-gallery__grid');
    if (!(grid instanceof HTMLElement)) return;

    let shown = 0;
    for (const item of this.#gridItemsOf(gallery)) {
      const itemState = this.#stateOf(item);
      const hide = this.#filtered && itemState !== 'any' && itemState !== state;
      item.classList.toggle('sh-paint-out', hide);
      if (!hide) shown += 1;
    }
    grid.dataset.shPaintCount = String(shown);
  }

  /** @param {HTMLElement} gallery */
  #clearGrid(gallery) {
    for (const item of this.#gridItemsOf(gallery)) item.classList.remove('sh-paint-out');
    const grid = gallery.querySelector('.media-gallery__grid');
    if (grid instanceof HTMLElement) delete grid.dataset.shPaintCount;
  }

  /**
   * @param {HTMLElement} gallery
   * @param {string} state
   */
  #jump(gallery, state) {
    const slideshow = /** @type {any} */ (this.#slideshowOf(gallery));
    if (!slideshow || typeof slideshow.select !== 'function') return;

    const slides = this.#slidesOf(gallery);
    const index = slides.findIndex((slide) => this.#stateOf(slide) === state);
    if (index < 0) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try {
      const result = slideshow.select(index, undefined, { animate: !reduceMotion });
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch {
      // A slideshow that isn't ready yet just doesn't move; the grid filter still applies.
    }
  }

  /**
   * Keeps the pressed button in step when the shopper swipes the visible slideshow.
   * @param {Event} event
   */
  #handleSlideSelect = (event) => {
    const detail = /** @type {any} */ (event).detail;
    const slide = detail?.slide;
    if (!(slide instanceof Element)) return;

    const slideshow = slide.closest('slideshow-component');
    if (!(slideshow instanceof HTMLElement) || slideshow.offsetParent === null) return;

    const state = this.#stateOf(slide);
    if (state === 'any' || state === this.#current) return;
    this.#apply(state, { jump: false });
  };

  /**
   * The gallery is swapped for a new one when the variant changes; re-bind once it's in place.
   * @param {Event} event
   */
  #handleProductSelect = (event) => {
    const promise = /** @type {any} */ (event).promise;
    if (!promise || typeof promise.then !== 'function') return;

    promise
      .then(() => requestAnimationFrame(() => this.isConnected && this.#bind()))
      .catch(() => {});
  };
}

if (!customElements.get('sh-paint-toggle')) {
  customElements.define('sh-paint-toggle', PaintToggle);
}
